/**
 * Continue with Google button — preserves the design system.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
 * REDIRECT URLS, THIS BREAKS THE AUTH.
 *
 * The redirect_origin MUST be derived from window.location.origin
 * at click time so users return to the exact domain they came from
 * (preview vs production). This is the contract Emergent's hosted
 * auth requires.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleSignInButton({
  label = "Continue with Google",
  testId = "google-signin-btn",
}) {
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    setLoading(true);
    try {
      const redirectOrigin = window.location.origin;
      const resp = await fetch(`${API}/auth/google/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          redirect_origin: redirectOrigin,
          provider: "emergent",
        }),
      });
      const data = await resp.json();
      if (data?.auth_url) {
        window.location.href = data.auth_url;
        return;
      }
      throw new Error("no auth_url");
    } catch (e) {
      setLoading(false);
      // Fall back to email/password silently — never block the user.
      console.warn("Google sign-in start failed", e);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      data-testid={testId}
      className="w-full h-11 flex items-center justify-center gap-3 bg-white hover:bg-slate-50 text-slate-900 font-medium rounded-lg transition-all active:scale-[0.98] disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
          />
          <path
            fill="#FF3D00"
            d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
          />
          <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
          />
        </svg>
      )}
      <span className="text-sm">{label}</span>
    </button>
  );
}
